import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getMailallianceAppDistributorAPI } from '@/proxy';

const getFolderChildren = (folderPath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerGetFolderChildren(folderPath);
};

const getBreadcrumb = (folderPath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerGetBreadcrumb(folderPath);
};

const createFolder = (parentFolderPath: string, name: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerCreateFolder(parentFolderPath, {
    name,
  });
};

const deleteFolder = (folderPath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerDeleteFolder(folderPath);
};

const getDownloadSasUrl = (filePath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerGetDownloadSasUrl(filePath);
};

const getUploadSasUrl = (filePath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerGetUploadSasUrl(filePath);
};

const deleteFile = (filePath: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerDeleteFile(filePath);
};

const updateFileOrder = (folderPath: string, order: string[]) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerUpdateFileOrder(folderPath, {
    order: order,
  });
};

const renameFile = (filePath: string, name: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerRenameFile(filePath, {
    name: name,
  });
};

const renameFolder = (folderPath: string, name: string) => {
  return getMailallianceAppDistributorAPI().blobStorageControllerRenameFolder(folderPath, {
    name: name,
  });
};

// React Query hooks
export const useGetFolderChildren = (folderPath: string) => {
  return useQuery({
    queryKey: ['blob-storage', 'folder-children', folderPath],
    queryFn: () => getFolderChildren(folderPath),
  });
};

export const useGetBreadcrumb = (folderPath: string) => {
  return useQuery({
    queryKey: ['blob-storage', 'breadcrumb', folderPath],
    queryFn: () => getBreadcrumb(folderPath),
  });
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ parentFolderPath, name }: { parentFolderPath: string; name: string }) =>
      createFolder(parentFolderPath, name),
    onSuccess: async (_, { parentFolderPath }) => {
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', parentFolderPath],
      });
    },
  });
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFolder,
    onSuccess: async (_, folderPath) => {
      const normalized = folderPath.replace(/\/$/, '');
      const lastSlashIndex = normalized.lastIndexOf('/');
      const parentPath = lastSlashIndex >= 0 ? normalized.substring(0, lastSlashIndex) : '';
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', parentPath],
      });
    },
  });
};

export const useGetDownloadSasUrl = () => {
  return useMutation({
    mutationFn: getDownloadSasUrl,
  });
};

export const useGetUploadSasUrl = () => {
  return useMutation({
    mutationFn: getUploadSasUrl,
  });
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFile,
    onSuccess: async (_, filePath) => {
      const normalized = filePath.replace(/\/$/, '');
      const lastSlashIndex = normalized.lastIndexOf('/');
      const parentPath = lastSlashIndex >= 0 ? normalized.substring(0, lastSlashIndex) : '';
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', parentPath],
      });
    },
  });
};

export const useUpdateFileOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderPath, order }: { folderPath: string; order: string[] }) =>
      updateFileOrder(folderPath, order),
    onSuccess: async (_, { folderPath }) => {
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', folderPath],
      });
    },
  });
};

export const useRenameFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ filePath, name }: { filePath: string; name: string }) =>
      renameFile(filePath, name),
    onSuccess: async (_, { filePath }) => {
      const normalized = filePath.replace(/\/$/, '');
      const lastSlashIndex = normalized.lastIndexOf('/');
      const parentPath = lastSlashIndex >= 0 ? normalized.substring(0, lastSlashIndex) : '';
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', parentPath],
      });
    },
  });
};

export const useRenameFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderPath, name }: { folderPath: string; name: string }) =>
      renameFolder(folderPath, name),
    onSuccess: async (_, { folderPath }) => {
      const normalized = folderPath.replace(/\/$/, '');
      const lastSlashIndex = normalized.lastIndexOf('/');
      const parentPath = lastSlashIndex >= 0 ? normalized.substring(0, lastSlashIndex) : '';
      await queryClient.invalidateQueries({
        queryKey: ['blob-storage', 'folder-children', parentPath],
      });
    },
  });
};
